/* =========================================================================
   MATCH-3 mini-game (Faith Match — "Gifts from Heaven"). Persistent tile
   nodes: identity moves between slots so swaps/falls animate. Pure grid logic
   lives in match3core. Typed port — identical behavior.
   ========================================================================= */
import { byId, show } from '../dom';
import { ctx } from '../context';
import { fmtDelta, shuffle } from '../engine';
import { sndTick, sndCorrect, sndJackpot, applause } from '../audio';
import {
  M3_COLS,
  M3_ROWS,
  M3_MOVES,
  m3at,
  m3rand,
  makeBoard,
  findMatches,
  swapWouldMatch,
  hasPossibleMove,
  match3Reward,
  type Board,
} from './match3core';
import { maybeShowTutorial, tutorialInfoButtonHTML } from '../tutorialOverlay';

const M3_SYMBOLS = ['🐟', '🫓', '✝️', '🕯️', '🕊️', '🔥'];   // rustic loaf + candle (was 🍞/🍇)
const SKIP_AWARD = 5_000;

export const match3ScreenHTML = /* html */ `
<div id="screenMatch3" class="match3 hidden">
  <div class="kicker">Halfway Break${tutorialInfoButtonHTML('match3')}</div>
  <h2>Faith Match</h2>
  <div class="blurb">Tap a symbol, then a neighbor, to swap. Line up <b>3+</b> to clear them — new gifts <b>fall from heaven</b>. You have <b>15 moves</b>.</div>
  <div class="manna-hud">
    <div class="hud-item"><span class="hud-k">Moves</span><span class="hud-v money" id="match3Moves">15</span></div>
    <div class="hud-item"><span class="hud-k">Cleared</span><span class="hud-v money" id="match3Score">0</span></div>
  </div>
  <div class="match3-field" id="match3Board"></div>
  <div class="result" id="match3Result"></div>
  <button class="skiplink" id="match3Skip">Skip this break →</button>
  <div class="cta"><button class="btn" id="match3Btn">Start Matching</button></div>
</div>`;

let match3Alive = false;
let match3Resolving = false;
let match3Started = false;
let match3Done = false;
let match3Moves = 0;
let match3Cleared = 0;
let match3BigClear = 0;
let match3Sel = -1;
let board: Board = [];
let boardEls: (HTMLElement | null)[] = [];

const m3delay = (ms: number): Promise<void> => new Promise((res) => setTimeout(res, ms));

interface Geom {
  size: number;
  ox: number;
  oy: number;
}

function m3Geom(): Geom {
  const b = byId('match3Board');
  const w = b.clientWidth,
    h = b.clientHeight || (w * M3_ROWS) / M3_COLS;
  const size = Math.floor(Math.min(w / M3_COLS, h / M3_ROWS));
  return { size, ox: Math.round((w - size * M3_COLS) / 2), oy: Math.round((h - size * M3_ROWS) / 2) };
}

function clearMatch3(): void {
  byId('match3Board')
    .querySelectorAll('.m3tile')
    .forEach((t) => t.remove());
  boardEls = [];
}

function m3NewTile(v: number): HTMLElement {
  const t = document.createElement('div');
  t.className = 'm3tile';
  t.textContent = M3_SYMBOLS[v];
  t.addEventListener('pointerdown', (e) => {
    e.preventDefault();
    const idx = boardEls.indexOf(t);
    if (idx >= 0) void onTileTap(idx);
  });
  byId('match3Board').appendChild(t);
  return t;
}

function m3SizeTile(t: HTMLElement, g: Geom): void {
  t.style.width = t.style.height = g.size + 'px';
  t.style.fontSize = Math.round(g.size * 0.58) + 'px';
}

function m3PosTile(t: HTMLElement, r: number, c: number, g: Geom): void {
  t.style.setProperty('--pos', `translate(${g.ox + c * g.size}px, ${g.oy + r * g.size}px)`);
}

function m3RenderAll(): void {
  const g = m3Geom();
  for (let i = 0; i < boardEls.length; i++) {
    const t = boardEls[i];
    if (!t) continue;
    m3SizeTile(t, g);
    m3PosTile(t, Math.floor(i / M3_COLS), i % M3_COLS, g);
    t.classList.toggle('sel', i === match3Sel);
  }
}

function m3CollapseDOM(): void {
  const g = m3Geom();
  for (let c = 0; c < M3_COLS; c++) {
    let write = M3_ROWS - 1;
    for (let r = M3_ROWS - 1; r >= 0; r--) {
      const i = m3at(r, c);
      if (board[i] !== null && board[i] !== undefined) {
        const w = m3at(write, c);
        if (w !== i) {
          board[w] = board[i];
          boardEls[w] = boardEls[i];
          board[i] = null;
          boardEls[i] = null;
        }
        write--;
      }
    }
    for (let r = write; r >= 0; r--) {
      const i = m3at(r, c);
      board[i] = m3rand();
      const t = m3NewTile(board[i] as number);
      m3SizeTile(t, g);
      t.style.setProperty('--pos', `translate(${g.ox + c * g.size}px, ${g.oy + (r - (write + 1)) * g.size}px)`);
      boardEls[i] = t;
    }
  }
  void byId('match3Board').offsetWidth;
}

export function showMatch3(): void {
  match3Alive = false;
  match3Resolving = false;
  match3Started = false;
  match3Done = false;
  match3Moves = M3_MOVES;
  match3Cleared = 0;
  match3BigClear = 0;
  match3Sel = -1;
  board = [];
  clearMatch3();
  byId('match3Moves').textContent = String(M3_MOVES);
  byId('match3Score').textContent = '0';
  byId('match3Result').innerHTML = '';
  const btn = byId<HTMLButtonElement>('match3Btn');
  btn.textContent = 'Start Matching';
  btn.disabled = false;
  btn.onclick = beginMatch3;
  show('match3');
  maybeShowTutorial('match3'); // tap-tap swap isn't the gesture most players expect
}

export function beginMatch3(): void {
  if (match3Started) return;
  match3Started = true;
  match3Alive = true;
  match3Moves = M3_MOVES;
  match3Cleared = 0;
  match3BigClear = 0;
  match3Sel = -1;
  const btn = byId<HTMLButtonElement>('match3Btn');
  btn.disabled = true;
  btn.textContent = 'Matching…';
  byId('match3Result').innerHTML = '';
  clearMatch3();
  do {
    board = makeBoard();
  } while (findMatches(board).size > 0 || !hasPossibleMove(board));
  const g = m3Geom();
  boardEls = new Array(board.length);
  for (let i = 0; i < board.length; i++) {
    const r = Math.floor(i / M3_COLS),
      c = i % M3_COLS;
    const t = m3NewTile(board[i] as number);
    m3SizeTile(t, g);
    t.style.setProperty('--pos', `translate(${g.ox + c * g.size}px, ${g.oy - (M3_ROWS - r) * g.size}px)`);
    boardEls[i] = t;
  }
  updateMatch3Hud();
  byId('match3Skip').classList.add('show');
  void byId('match3Board').offsetWidth;
  requestAnimationFrame(m3RenderAll);
}

function updateMatch3Hud(): void {
  byId('match3Moves').textContent = String(match3Moves);
  byId('match3Score').textContent = String(match3Cleared);
}

function m3Swap(a: number, b: number): void {
  [board[a], board[b]] = [board[b], board[a]];
  [boardEls[a], boardEls[b]] = [boardEls[b], boardEls[a]];
}

async function onTileTap(i: number): Promise<void> {
  if (!match3Alive || match3Resolving || match3Moves <= 0) return;
  if (match3Sel === -1) {
    match3Sel = i;
    m3RenderAll();
    return;
  }
  if (match3Sel === i) {
    match3Sel = -1;
    m3RenderAll();
    return;
  }
  const r1 = Math.floor(match3Sel / M3_COLS),
    c1 = match3Sel % M3_COLS;
  const r2 = Math.floor(i / M3_COLS),
    c2 = i % M3_COLS;
  const adjacent = Math.abs(r1 - r2) + Math.abs(c1 - c2) === 1;
  if (!adjacent) {
    match3Sel = i;
    m3RenderAll();
    return;
  }

  const a = match3Sel,
    b2 = i;
  match3Sel = -1;
  if (!swapWouldMatch(board, a, b2)) {
    match3Resolving = true;
    m3Swap(a, b2);
    m3RenderAll();
    sndTick();
    await m3delay(230);
    if (!match3Alive) return;
    m3Swap(a, b2);
    m3RenderAll();
    await m3delay(230);
    if (!match3Alive) return;
    match3Resolving = false;
    return;
  }
  match3Resolving = true;
  match3Moves--;
  m3Swap(a, b2);
  m3RenderAll();
  sndTick();
  updateMatch3Hud();
  await m3delay(210);
  if (!match3Alive) return;
  await resolveBoard();
  if (!match3Alive) return;

  if (match3Moves > 0 && !hasPossibleMove(board)) {
    byId('match3Result').innerHTML = `<div class="r-sub">No moves — shuffling from heaven…</div>`;
    reshuffleBoardDOM();
    m3RenderAll();
    await m3delay(300);
    if (!match3Alive) return;
    byId('match3Result').innerHTML = '';
  }
  match3Resolving = false;
  if (match3Moves <= 0) finishMatch3();
}

function reshuffleBoardDOM(): void {
  let order: number[];
  let trial: Board = [];
  do {
    order = shuffle(board.map((_, i) => i));
    trial = new Array(board.length);
    for (let k = 0; k < order.length; k++) trial[k] = board[order[k]];
  } while (findMatches(trial).size > 0 || !hasPossibleMove(trial));
  const newVals: Board = new Array(board.length);
  const newEls: (HTMLElement | null)[] = new Array(board.length);
  for (let k = 0; k < order.length; k++) {
    newVals[k] = board[order[k]];
    newEls[k] = boardEls[order[k]];
  }
  board = newVals;
  boardEls = newEls;
}

async function resolveBoard(): Promise<void> {
  let chain = 0;
  for (;;) {
    const m = findMatches(board);
    if (m.size === 0) break;
    chain++;
    match3Cleared += m.size;
    match3BigClear = Math.max(match3BigClear, m.size);
    updateMatch3Hud();
    chain >= 3 ? sndCorrect() : sndTick();
    m.forEach((idx) => {
      if (boardEls[idx]) boardEls[idx]!.classList.add('clearing');
    });
    await m3delay(200);
    if (!match3Alive) return;
    m.forEach((idx) => {
      if (boardEls[idx]) boardEls[idx]!.remove();
      boardEls[idx] = null;
      board[idx] = null;
    });
    m3CollapseDOM();
    m3RenderAll();
    await m3delay(240);
    if (!match3Alive) return;
  }
}

function finishMatch3(): void {
  if (match3Done) return;
  match3Done = true;
  match3Alive = false;
  match3Sel = -1;
  byId('match3Skip').classList.remove('show');
  let cents = match3Reward(match3Cleared),
    jackpot = false;
  if (match3BigClear >= 8) {
    cents = 20_000;
    jackpot = true;
    ctx.addAchievement('🌊', 'Heavenly Cascade', 'Cleared 8+ gifts in a single cascade');
  }
  ctx.engine?.awardBonus(cents, match3Cleared + ' gifts', 'Match3');
  ctx.updateBank();
  ctx.floatDelta(cents);
  if (jackpot) {
    sndJackpot();
    applause();
  } else {
    sndCorrect();
  }
  byId('match3Result').innerHTML =
    `<div class="r-amt ${jackpot ? 'fast' : ''}">${jackpot ? 'BINGO! ' + fmtDelta(cents) : fmtDelta(cents)}</div>` +
    `<div class="r-sub">Cleared ${match3Cleared} gifts${jackpot ? ' — a heavenly cascade!' : ''}</div>`;
  const btn = byId<HTMLButtonElement>('match3Btn');
  btn.textContent = 'Continue';
  btn.disabled = false;
  btn.onclick = () => {
    btn.onclick = beginMatch3;
    ctx.nextQuestion();
  };
}

export function skipMatch3(): void {
  if (!match3Alive || match3Done) return;
  match3Done = true;
  match3Alive = false;
  match3Sel = -1;
  ctx.engine?.awardBonus(SKIP_AWARD, 'a peaceful pass', 'Skip');
  ctx.updateBank();
  ctx.floatDelta(SKIP_AWARD);
  sndCorrect();
  byId('match3Result').innerHTML = `<div class="r-amt">${fmtDelta(SKIP_AWARD)}</div><div class="r-sub">Skipped. On to the next round.</div>`;
  const btn = byId<HTMLButtonElement>('match3Btn');
  btn.textContent = 'Continue';
  btn.disabled = false;
  btn.onclick = () => {
    btn.onclick = beginMatch3;
    ctx.nextQuestion();
  };
  byId('match3Skip').classList.remove('show');
}
